import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { parse } from "date-fns";

const API_BACKEND_URL: string = import.meta.env
  .VITE_ORDER_PORTAL_API_BACKEND_URL;
const API_KEY: string = import.meta.env.VITE_ORDER_PORTAL_API_KEY;

export const orderPortalApi = createApi({
  reducerPath: "orderPortalApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BACKEND_URL}/order_portal`,
    prepareHeaders: (headers) => {
      if (API_KEY) {
        headers.set("x-api-key", API_KEY);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (data) => ({
        url: "/dealer_login",
        method: "POST",
        body: data,
      }),
    }),
    getServiceProviders: builder.query<
      GetServiceProvidersResponse,
      GetServiceProvidersRequest
    >({
      query: (data) => ({
        url: "/get_provider",
        method: "POST",
        body: data,
      }),
    }),
    checkBlacklist: builder.query<boolean, CheckBlacklistRequest>({
      query: (data) => ({
        url: "/get_black_list",
        method: "POST",
        body: data,
      }),
    }),
    getPlanOfferings: builder.query<
      GetPlanOfferingsResponse,
      GetPlanOfferingsRequest
    >({
      query: (data) => ({
        url: "/get_offerings_list",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: GetPlanOfferingsResponse) => {
        function parsePrice(price: string | undefined) {
          if (!price) {
            return 0;
          }

          const numeric = Number(
            price.replace(/[^0-9.,-]+/g, "").replace(/,/g, "")
          );

          return Number.isNaN(numeric) ? 0 : numeric;
        }

        const filteredCategories = [...response].filter(
          (category) => category.name.trim() !== "12 Months Contract Plans"
        );

        const sortedCategories: GetPlanOfferingsResponse = filteredCategories
          .map((category) => {
            const sortedMembers = [...category.member].sort(
              (memberA, memberB) =>
                parsePrice(memberA.price) - parsePrice(memberB.price)
            );

            return {
              ...category,
              member: sortedMembers,
            } as typeof category;
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        return sortedCategories;
      },
    }),
    getDeviceAvailability: builder.query<
      GetDeviceAvailabilityResponse,
      GetDeviceAvailabilityRequest
    >({
      query: (data) => ({
        url: "/get_item_stock",
        method: "POST",
        body: data,
      }),
    }),
    bookDevice: builder.mutation<number, BookDeviceRequest>({
      query: (data) => ({
        url: "/book_device",
        method: "POST",
        body: data,
      }),
    }),
    getAvailableMSISDNs: builder.query<
      GetAvailableMSISDNsResponse,
      GetAvailableMSISDNsRequest
    >({
      query: (data) => ({
        url: "/search_number",
        method: "POST",
        body: data,
      }),
    }),
    bookMSISDN: builder.mutation<boolean, BookMSISDNRequest>({
      query: (data) => ({
        url: "/book_number",
        method: "POST",
        body: data,
      }),
    }),
    getCountries: builder.query<GetCountriesResponse, GetCountriesRequest>({
      query: (data) => ({
        url: "/get_countries",
        method: "POST",
        body: data,
      }),
    }),
    getPublicHolidays: builder.query<Date[], GetPublicHolidaysRequest>({
      query: (data) => ({
        url: "/get_public_holiday",
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: GenericAPIResponse<GetPublicHolidaysResponse>
      ): Date[] =>
        response.response.data.map((holiday) =>
          parse(holiday.holiday_date, "yyyy-MM-dd", new Date())
        ),
    }),
    getStores: builder.query<GetStoresResponse, GetStoresRequest>({
      query: (data) => ({
        url: "/get_counter_list",
        method: "POST",
        body: data,
      }),
    }),
    getAddresses: builder.query<GetAddressesResponse, GetAddressesRequest>({
      query: (data) => ({
        url: "/get_address_list",
        method: "POST",
        body: data,
      }),
    }),
    getPostalCodes: builder.query<
      GetPostalCodesResponse,
      GetPostalCodesRequest
    >({
      query: (data) => ({
        url: "/get_postal_code",
        method: "POST",
        body: data,
      }),
    }),
    referAFriendValidation: builder.query<
      ReferAFriendValidationResponse,
      ReferAFriendValidationRequest
    >({
      query: (data) => ({
        url: "/validate_referral",
        method: "POST",
        body: data,
      }),
    }),
    sendSMSOTP: builder.mutation<SendSMSOTPResponse, SendSMSOTPRequest>({
      query: (data) => ({
        url: "/send_sms_otp",
        method: "POST",
        body: data,
      }),
    }),
    verifySMSOTP: builder.mutation<boolean, VerifySMSOTPRequest>({
      query: (data) => ({
        url: "/verification_otp",
        method: "POST",
        body: data,
      }),
    }),
    submitOrder: builder.mutation<SubmitOrderResponse, SubmitOrderRequest>({
      query: (data) => ({
        url: "/create_order_portal",
        method: "POST",
        body: data,
      }),
    }),
    uploadCustomerFiles: builder.mutation<
      UploadCustomerFilesResponse,
      FormData
    >({
      query: (data) => ({
        url: "/upload_file_identity",
        method: "POST",
        body: data,
      }),
    }),
    createCheckoutSession: builder.query<
      CreateCheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: (data) => ({
        url: "/checkout_session",
        method: "POST",
        body: data,
      }),
    }),
    payWithWallet: builder.mutation<
      PayWithWalletResponse,
      PayWithWalletRequest
    >({
      query: (data) => ({
        url: "/dealer_pay_by_wallet",
        method: "POST",
        body: data,
      }),
    }),
    getOrderDetails: builder.query<
      GetOrderDetailsResponse,
      GetOrderDetailsRequest
    >({
      query: (data) => ({
        url: "/get_order_details",
        method: "POST",
        body: data,
      }),
    }),
    getEasiRechargeOptions: builder.query<
      GetEasiRechargeOptionsResponse,
      GetEasiRechargeOptionsRequest
    >({
      query: (data) => ({
        url: "/get_easi_recharge_options",
        method: "POST",
        body: data,
      }),
    }),
    checkCustomer: builder.query<CheckCustomerResponse, CheckCustomerRequest>({
      query: (data) => ({
        url: "/check_customer",
        method: "POST",
        body: data,
      }),
    }),
    easiRecharge: builder.mutation<EasiRechargeResponse, EasiRechargeRequest>({
      query: (data) => ({
        url: "/easi_recharge",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetServiceProvidersQuery,
  useLazyCheckBlacklistQuery,
  useGetPlanOfferingsQuery,
  useLazyGetDeviceAvailabilityQuery,
  useBookDeviceMutation,
  useLazyGetAvailableMSISDNsQuery,
  useBookMSISDNMutation,
  useGetCountriesQuery,
  useGetPublicHolidaysQuery,
  useGetStoresQuery,
  useLazyGetAddressesQuery,
  useLazyGetPostalCodesQuery,
  useLazyReferAFriendValidationQuery,
  useSendSMSOTPMutation,
  useVerifySMSOTPMutation,
  useSubmitOrderMutation,
  useUploadCustomerFilesMutation,
  useCreateCheckoutSessionQuery,
  usePayWithWalletMutation,
  useGetOrderDetailsQuery,
  useGetEasiRechargeOptionsQuery,
  useLazyCheckCustomerQuery,
  useEasiRechargeMutation,
} = orderPortalApi;
